import { type Vec2, wrapIndex } from '../core/math';
import type { Track } from '../track/generate';
import type { Corner } from '../track/brakingzones';
import { brakingZoneRange } from '../track/brakingzones';
import type { SpeedProfile } from '../track/speedprofile';
import type { Weather } from '../track/weather';
import { offsetRing } from '../track/geometry';
import type { RunoffKind } from '../track/elevation';
import { placeLandmarks } from '../track/landmarks';
import {
  BILLBOARD_H,
  BILLBOARD_W,
  buildSponsorship,
  type Sponsor,
  type TrackSponsorship,
} from '../track/sponsors';
import type { Camera } from './camera';
import { readableAngle, sponsorPanel } from './sponsorArt';

export const COLORS = {
  grass: '#16281d',
  grassAlt: '#1a2f22',
  /** A trap, not a lawn — dry, warm-toned, unmistakably not grass at any
   *  zoom. Differs from grass mainly in lightness, so no colourblind override
   *  is needed the way the red/green pairs below get one. */
  gravel: '#8a7f68',
  asphalt: '#35383e',
  /** Paved run-off just outside the track edge — real circuits give a spun kart
   *  a metre or so of grip before the grass, and the darker band reads as
   *  surface depth even at a standstill. */
  shoulder: '#282b2f',
  asphaltEdge: '#e8e8ea',
  kerbA: '#c8352f',
  kerbB: '#e9e9ec',
  brakeZone: '#ff8a3d',
  brakeLine: '#ffd23d',
  boardFace: '#f2f2f4',
  boardText: '#16181c',
  startLine: '#f2f2f4',
  water: '#1b3a4a',
  sectorLine: '#7ba7d4',
  wallRail: '#c9ced6',
  wallPost: '#7c828b',
};

/**
 * The braking-zone overlay and its brake line lean on an orange/red ramp, which
 * is the single worst pairing for red-green colour blindness. This swaps them
 * for a blue→yellow scale that stays distinguishable, and lightens the kerbs for
 * the same reason.
 */
export const COLORBLIND_COLORS: typeof COLORS = {
  ...COLORS,
  kerbA: '#3f6fb5',
  brakeZone: '#4d9be6',
  brakeLine: '#ffe066',
  water: '#26485c',
  wallRail: '#b9c7dc',
  wallPost: '#6f7f95',
};

/**
 * The colours to lay along a circuit's kerbs, in order, one per ~6 m block.
 *
 * Most circuits really are just red and white, so that stays the default and
 * an unauthored circuit is untouched. A circuit that authors its own gets it —
 * except under the colourblind palette, which wins outright: a kerb marks the
 * track limit, and that is information, not livery. Both renderers call this
 * so a kerb cannot be one colour in the overhead view and another in 3D.
 */
export function kerbCycle(track: Track, colorblind: boolean): readonly string[] {
  const c = palette(colorblind);
  if (colorblind || !track.kerbColors?.length) return [c.kerbA, c.kerbB];
  return track.kerbColors;
}

export function palette(colorblind: boolean): typeof COLORS {
  return colorblind ? COLORBLIND_COLORS : COLORS;
}

function ringPath(outer: Vec2[], inner: Vec2[]): Path2D {
  const p = new Path2D();
  p.moveTo(outer[0].x, outer[0].y);
  for (let i = 1; i < outer.length; i++) p.lineTo(outer[i].x, outer[i].y);
  p.closePath();
  p.moveTo(inner[0].x, inner[0].y);
  for (let i = inner.length - 1; i >= 0; i--) p.lineTo(inner[i].x, inner[i].y);
  p.closePath();
  return p;
}

function linePath(pts: Vec2[]): Path2D {
  const p = new Path2D();
  p.moveTo(pts[0].x, pts[0].y);
  for (let i = 1; i < pts.length; i++) p.lineTo(pts[i].x, pts[i].y);
  p.closePath();
  return p;
}

interface ZoneQuad {
  path: Path2D;
  alpha: number;
}

/** A sponsor wordmark placed in world metres — position, size and the angle it
 *  reads at. Drawn as an image so the type stays crisp at any zoom instead of
 *  being re-laid-out every frame at a fractional font size. */
interface Decal {
  img: HTMLCanvasElement;
  x: number;
  y: number;
  angle: number;
  w: number;
  h: number;
  /** Cull radius, metres. */
  pad: number;
}

/**
 * All static track geometry is built into Path2D objects once and re-filled
 * under the camera transform each frame. That keeps it crisp at any zoom without
 * the memory cost of pre-rendering a 300 m circuit to a bitmap.
 */
export class TrackRenderer {
  private readonly road: Path2D;
  private readonly shoulder: Path2D;
  private readonly edgeL: Path2D;
  private readonly edgeR: Path2D;
  private wallL!: Path2D;
  private wallR!: Path2D;
  private readonly wallPosts: Array<{ x: number; y: number; side: 1 | -1 }> = [];
  /** One path per colour in the circuit's kerb cycle — two for the default
   *  red/white, more where a circuit authors its own. */
  private readonly kerbPaths: Path2D[] = [];
  private kerbColours: readonly string[] = [COLORS.kerbA, COLORS.kerbB];
  private readonly zones: ZoneQuad[] = [];
  private readonly brakeLines: Path2D[] = [];
  private readonly startBand: Path2D;
  private readonly boards: Array<{ x: number; y: number; label: string }> = [];

  private readonly water: ZoneQuad[] = [];
  private readonly sectorBands: Path2D[] = [];
  /** Gravel and paved run-off, where a real circuit authors something other
   *  than plain grass beyond the kerb — see docs/circuit-fidelity.md §6.
   *  Empty (and invisible) on every circuit that hasn't authored `runoff`. */
  private readonly runoffBands: Array<{ path: Path2D; kind: RunoffKind }> = [];
  /** Painted sponsor run-off outside selected corners. */
  private readonly groundPaint: Array<{ path: Path2D; sponsor: Sponsor }> = [];
  /** Every sponsor wordmark that is blitted into the world transform. */
  private readonly decals: Decal[] = [];
  /** Plan-view footprints of the circuit's own scenery — see
   *  `src/track/landmarks.ts`. Empty on every circuit that authors none. */
  private readonly landmarkShapes: Array<{ path: Path2D; fill: string; edge: string }> = [];
  /** Coloured backing for the barrier ads, hugging the armco's own curve. */
  private readonly barrierPaint: Array<{ path: Path2D; sponsor: Sponsor; x: number; y: number }> = [];
  private c = COLORS;

  constructor(
    private readonly track: Track,
    private readonly corners: Corner[],
    private readonly profile: SpeedProfile,
    private readonly weather?: Weather,
  ) {
    const { center, normal, width, kerbWidth } = track;
    const half = width / 2;

    this.road = ringPath(offsetRing(center, normal, half), offsetRing(center, normal, -half));
    // A metre of paved shoulder outside the road on both sides — this ribbon
    // is a superset of `road`'s, so filling it first and the road on top
    // leaves only the outer band showing, no separate hole geometry needed.
    const shoulderHalf = half + 1;
    this.shoulder = ringPath(
      offsetRing(center, normal, shoulderHalf),
      offsetRing(center, normal, -shoulderHalf),
    );
    this.edgeL = linePath(offsetRing(center, normal, half - 0.12));
    this.edgeR = linePath(offsetRing(center, normal, -(half - 0.12)));

    this.kerbColours = kerbCycle(track, false);
    this.buildKerbs(half, kerbWidth);
    this.buildRunoffBands(half, kerbWidth);
    this.buildWalls();
    this.buildZones(half);
    this.startBand = this.buildStartBand(half);
    this.buildBoards(half, kerbWidth);
    this.buildWater(half);
    this.buildSectorBands(half);
    this.buildSponsors();
    this.buildLandmarks();
  }

  /**
   * Landmarks seen from directly overhead, which is all this view can honestly
   * show of them: a ferris wheel is a circle and a grandstand is a rectangle
   * from up here. Same placement the 3D renderer uses, so the two agree about
   * where a thing stands even though only one of them can show what it is.
   */
  private buildLandmarks(): void {
    for (const l of placeLandmarks(this.track, this.corners)) {
      const path = new Path2D();
      if (l.kind === 'wheel' || l.kind === 'bull') {
        path.arc(l.x, l.y, l.radius, 0, Math.PI * 2);
      } else {
        // Long side along the track, which is how a stand or a tower sits.
        const ax = l.tx;
        const ay = l.ty;
        const bx = -ay * l.side;
        const by = ax * l.side;
        const half = l.kind === 'grandstand' ? 14 * l.scale : 8 * l.scale;
        const depth = l.kind === 'banking' ? 9 * l.scale : 5 * l.scale;
        const corner = (a: number, d: number): [number, number] => [
          l.x + ax * a + bx * d,
          l.y + ay * a + by * d,
        ];
        const p0 = corner(-half, -depth / 2);
        const p1 = corner(half, -depth / 2);
        const p2 = corner(half, depth / 2);
        const p3 = corner(-half, depth / 2);
        path.moveTo(p0[0], p0[1]);
        path.lineTo(p1[0], p1[1]);
        path.lineTo(p2[0], p2[1]);
        path.lineTo(p3[0], p3[1]);
        path.closePath();
      }
      this.landmarkShapes.push({ path, fill: '#3c424b', edge: '#6a707a' });
    }
  }

  setPalette(colorblind: boolean): void {
    this.c = palette(colorblind);
    this.kerbColours = kerbCycle(this.track, colorblind);
  }

  /** Standing water, drawn per sample so a patch's edges taper like its grip. */
  private buildWater(half: number): void {
    if (!this.weather) return;
    const { center, normal } = this.track;
    const n = center.length;
    for (let i = 0; i < n; i++) {
      const depth = this.weather.patches[i];
      if (depth < 0.02) continue;
      const j = wrapIndex(i + 1, n);
      const quad = new Path2D();
      quad.moveTo(center[i].x + normal[i].x * half, center[i].y + normal[i].y * half);
      quad.lineTo(center[j].x + normal[j].x * half, center[j].y + normal[j].y * half);
      quad.lineTo(center[j].x - normal[j].x * half, center[j].y - normal[j].y * half);
      quad.lineTo(center[i].x - normal[i].x * half, center[i].y - normal[i].y * half);
      quad.closePath();
      this.water.push({ path: quad, alpha: Math.min(0.62, depth * 1.4) });
    }
  }

  private buildSectorBands(half: number): void {
    const { center, normal, sectorStarts } = this.track;
    const n = center.length;
    // Skip sector 1, which sits on the start line and would double up.
    for (let s = 1; s < sectorStarts.length; s++) {
      const a = sectorStarts[s];
      const b = wrapIndex(a + 1, n);
      const p = new Path2D();
      p.moveTo(center[a].x + normal[a].x * half, center[a].y + normal[a].y * half);
      p.lineTo(center[a].x - normal[a].x * half, center[a].y - normal[a].y * half);
      p.lineTo(center[b].x - normal[b].x * half, center[b].y - normal[b].y * half);
      p.lineTo(center[b].x + normal[b].x * half, center[b].y + normal[b].y * half);
      p.closePath();
      this.sectorBands.push(p);
    }
  }

  private buildKerbs(half: number, kerbWidth: number): void {
    const { center, normal, curvature } = this.track;
    const n = center.length;
    const blockSteps = 3; // ~6 m of kerb per colour block

    const buckets = Math.max(2, this.track.kerbColors?.length ?? 2);
    for (let b = 0; b < buckets; b++) this.kerbPaths.push(new Path2D());

    for (let i = 0; i < n; i += blockSteps) {
      // Kerbing belongs on corners; a fully kerbed perimeter reads as noise.
      const kappa = Math.abs(curvature[i]);
      if (kappa < 1 / 60) continue;

      const j = Math.min(i + blockSteps, n);
      const target = this.kerbPaths[(i / blockSteps) % this.kerbPaths.length];

      for (const side of [1, -1]) {
        const inner = half;
        const outer = half + kerbWidth;
        target.moveTo(
          center[i].x + normal[i].x * inner * side,
          center[i].y + normal[i].y * inner * side,
        );
        for (let k = i + 1; k <= j; k++) {
          const idx = wrapIndex(k, n);
          target.lineTo(
            center[idx].x + normal[idx].x * inner * side,
            center[idx].y + normal[idx].y * inner * side,
          );
        }
        for (let k = j; k >= i; k--) {
          const idx = wrapIndex(k, n);
          target.lineTo(
            center[idx].x + normal[idx].x * outer * side,
            center[idx].y + normal[idx].y * outer * side,
          );
        }
        target.closePath();
      }
    }
  }

  /**
   * Gravel and paved run-off between the kerb and the barrier, on the real
   * circuits that authored something other than plain grass there. Adjacent
   * same-kind samples merge into one ring so a long gravel trap is one fill
   * call rather than hundreds; a `grass` run is skipped entirely since the
   * grass background already reads correctly underneath it.
   */
  private buildRunoffBands(half: number, kerbWidth: number): void {
    const kinds = this.track.runoffKind;
    if (!kinds) return;
    const { center, normal } = this.track;
    const n = center.length;
    const inner = half + kerbWidth;

    for (const side of [1, -1] as const) {
      // Run-off ends at the barrier, and the barrier moves — see
      // src/track/barrier.ts.
      const ring = side > 0 ? this.track.barriers.left : this.track.barriers.right;
      let runStart = -1;
      let runKind: RunoffKind | null = null;

      const flush = (end: number): void => {
        if (runStart < 0 || runKind === null || runKind === 'grass') return;
        const path = new Path2D();
        const a0 = wrapIndex(runStart, n);
        path.moveTo(
          center[a0].x + normal[a0].x * inner * side,
          center[a0].y + normal[a0].y * inner * side,
        );
        for (let k = runStart; k <= end; k++) {
          const idx = wrapIndex(k, n);
          path.lineTo(center[idx].x + normal[idx].x * inner * side, center[idx].y + normal[idx].y * inner * side);
        }
        for (let k = end; k >= runStart; k--) {
          const idx = wrapIndex(k, n);
          const outer = ring.dist[idx];
          path.lineTo(center[idx].x + normal[idx].x * outer * side, center[idx].y + normal[idx].y * outer * side);
        }
        path.closePath();
        this.runoffBands.push({ path, kind: runKind });
      };

      for (let i = 0; i <= n; i++) {
        const kind = i < n ? kinds[wrapIndex(i, n)] : null;
        if (kind !== runKind) {
          if (runStart >= 0) flush(i - 1);
          runStart = kind !== null ? i : -1;
          runKind = kind;
        }
      }
    }
  }

  /** Low armco wall exactly where the physics clamps (clampToBarrier). A rail
   *  strip on each side plus posts — visible so the limit is legible, since a
   *  barrier you cannot see reads as an invisible wall. */
  private buildWalls(): void {
    const { center, normal } = this.track;
    const n = center.length;

    // One stroked path per side, but broken into subpaths: the barrier is not
    // a constant offset any more, and where it could not be pulled clear of
    // another part of the lap it is not drawn at all. See src/track/barrier.ts.
    const buildRing = (side: 1 | -1): Path2D => {
      const bar = side > 0 ? this.track.barriers.left : this.track.barriers.right;
      const p = new Path2D();
      let open = false;
      for (let i = 0; i <= n; i++) {
        const idx = wrapIndex(i, n);
        if (!bar.drawn[idx]) {
          open = false;
          continue;
        }
        const d = bar.dist[idx] - 0.3;
        const x = center[idx].x + normal[idx].x * d * side;
        const y = center[idx].y + normal[idx].y * d * side;
        if (open) p.lineTo(x, y);
        else p.moveTo(x, y);
        open = true;
      }
      return p;
    };

    this.wallL = buildRing(1);
    this.wallR = buildRing(-1);

    for (let i = 0; i < n; i += 14) {
      const idx = i % n;
      for (const side of [1, -1] as const) {
        const bar = side > 0 ? this.track.barriers.left : this.track.barriers.right;
        if (!bar.drawn[idx]) continue;
        const d = bar.dist[idx] - 0.3;
        this.wallPosts.push({
          x: center[idx].x + normal[idx].x * d * side,
          y: center[idx].y + normal[idx].y * d * side,
          side,
        });
      }
    }
  }

  /**
   * The zone is drawn as one quad per centerline step with alpha ramping toward
   * the corner, so the road visibly darkens as the braking point approaches
   * rather than switching on at a hard edge.
   */
  private buildZones(half: number): void {
    const { center, normal } = this.track;
    const n = center.length;

    for (const corner of this.corners) {
      const range = brakingZoneRange(this.track, corner);
      if (range.length < 2) continue;

      for (let s = 0; s < range.length - 1; s++) {
        const i = range[s];
        const j = range[s + 1];
        const t = s / (range.length - 1);
        const quad = new Path2D();
        quad.moveTo(center[i].x + normal[i].x * half, center[i].y + normal[i].y * half);
        quad.lineTo(center[j].x + normal[j].x * half, center[j].y + normal[j].y * half);
        quad.lineTo(center[j].x - normal[j].x * half, center[j].y - normal[j].y * half);
        quad.lineTo(center[i].x - normal[i].x * half, center[i].y - normal[i].y * half);
        quad.closePath();
        this.zones.push({ path: quad, alpha: 0.08 + t * 0.24 });
      }

      const b = corner.brakeIndex;
      const nb = wrapIndex(b + 1, n);
      const line = new Path2D();
      line.moveTo(center[b].x + normal[b].x * half, center[b].y + normal[b].y * half);
      line.lineTo(center[b].x - normal[b].x * half, center[b].y - normal[b].y * half);
      line.lineTo(center[nb].x - normal[nb].x * half, center[nb].y - normal[nb].y * half);
      line.lineTo(center[nb].x + normal[nb].x * half, center[nb].y + normal[nb].y * half);
      line.closePath();
      this.brakeLines.push(line);
    }
  }

  private buildStartBand(half: number): Path2D {
    const { center, normal, startIndex } = this.track;
    const n = center.length;
    const a = startIndex;
    const b = wrapIndex(startIndex + 1, n);
    const p = new Path2D();
    p.moveTo(center[a].x + normal[a].x * half, center[a].y + normal[a].y * half);
    p.lineTo(center[a].x - normal[a].x * half, center[a].y - normal[a].y * half);
    p.lineTo(center[b].x - normal[b].x * half, center[b].y - normal[b].y * half);
    p.lineTo(center[b].x + normal[b].x * half, center[b].y + normal[b].y * half);
    p.closePath();
    return p;
  }

  /**
   * Trackside sponsorship: painted run-off, barrier panels and freestanding
   * billboards. Placement comes from `src/track/sponsors.ts`, which the 3D
   * renderer reads too, so an ad is at the same metre of track in both.
   *
   * Seen from directly overhead a vertical billboard would be a hairline, so
   * the 2D view lays each panel's face flat on the ground at its real size —
   * the same convention the countdown boards already use, and the only way the
   * logo is a logo rather than a line. Wordmarks are pre-painted canvases
   * blitted under the camera transform; nothing here lays out text per frame.
   *
   * Sponsor colours are decorative and sit entirely outside the racing
   * surface, so unlike the kerbs or the braking overlay they carry no meaning
   * that a colourblind palette would have to preserve.
   */
  private buildSponsors(): void {
    const { center, normal } = this.track;
    const sp: TrackSponsorship = buildSponsorship(this.track, this.corners);

    // Painted corner run-off, edge-to-edge between kerb and barrier.
    for (const ad of sp.groundAds) {
      for (const run of ad.runs) {
        const path = new Path2D();
        const first = run.indices[0];
        path.moveTo(
          center[first].x + normal[first].x * run.inner[0] * ad.side,
          center[first].y + normal[first].y * run.inner[0] * ad.side,
        );
        for (let s = 1; s < run.indices.length; s++) {
          const i = run.indices[s];
          path.lineTo(
            center[i].x + normal[i].x * run.inner[s] * ad.side,
            center[i].y + normal[i].y * run.inner[s] * ad.side,
          );
        }
        for (let s = run.indices.length - 1; s >= 0; s--) {
          const i = run.indices[s];
          path.lineTo(
            center[i].x + normal[i].x * run.outer[s] * ad.side,
            center[i].y + normal[i].y * run.outer[s] * ad.side,
          );
        }
        path.closePath();
        this.groundPaint.push({ path, sponsor: ad.sponsor });
      }
      this.decals.push(
        this.decalAt(ad.labelIndex, ad.labelOffset * ad.side, ad.sponsor, ad.labelW, ad.labelH, false),
      );
    }

    // Barrier panels: a coloured band on the armco line, wordmark on top.
    const bandHalf = 0.75;
    const wallAt = (idx: number, side: 1 | -1): number =>
      (side > 0 ? this.track.barriers.left : this.track.barriers.right).dist[idx] - 0.3;
    for (const ad of sp.barrierAds) {
      const path = new Path2D();
      const edge = (delta: number, reverse: boolean): void => {
        const order = reverse ? [...ad.indices].reverse() : ad.indices;
        for (const i of order) {
          const offset = wallAt(i, ad.side) + delta;
          const x = center[i].x + normal[i].x * offset * ad.side;
          const y = center[i].y + normal[i].y * offset * ad.side;
          path.lineTo(x, y);
        }
      };
      const start = ad.indices[0];
      path.moveTo(
        center[start].x + normal[start].x * (wallAt(start, ad.side) - bandHalf) * ad.side,
        center[start].y + normal[start].y * (wallAt(start, ad.side) - bandHalf) * ad.side,
      );
      edge(-bandHalf, false);
      edge(bandHalf, true);
      path.closePath();

      const mid = ad.indices[Math.floor(ad.indices.length / 2)];
      const wallDist = wallAt(mid, ad.side);
      this.barrierPaint.push({
        path,
        sponsor: ad.sponsor,
        x: center[mid].x + normal[mid].x * wallDist * ad.side,
        y: center[mid].y + normal[mid].y * wallDist * ad.side,
      });
      // The band spans one fewer gap than it has samples; sizing the wordmark
      // off the sample count instead overhangs both ends of its own panel.
      const adLen = (ad.indices.length - 1) * this.track.spacing;
      this.decals.push(this.decalAt(mid, wallDist * ad.side, ad.sponsor, adLen * 0.88, bandHalf * 1.5, false));
    }

    // Freestanding billboards, past the barrier.
    for (const b of sp.billboards) {
      this.decals.push({
        img: sponsorPanel(b.sponsor, { wMetres: BILLBOARD_W, hMetres: BILLBOARD_H }),
        x: b.x,
        y: b.y,
        angle: readableAngle(b.tx, b.ty),
        w: BILLBOARD_W,
        h: BILLBOARD_H,
        pad: BILLBOARD_W,
      });
    }
  }

  /** A wordmark laid flat on the ground beside sample `index`, `offset` metres
   *  along the normal (sign included). */
  private decalAt(
    index: number,
    offset: number,
    sponsor: Sponsor,
    w: number,
    h: number,
    background: boolean,
  ): Decal {
    const { center, normal } = this.track;
    const n = center.length;
    const prev = center[wrapIndex(index - 1, n)];
    const next = center[wrapIndex(index + 1, n)];
    return {
      img: sponsorPanel(sponsor, { wMetres: w, hMetres: h, background }),
      x: center[index].x + normal[index].x * offset,
      y: center[index].y + normal[index].y * offset,
      angle: readableAngle(next.x - prev.x, next.y - prev.y),
      w,
      h,
      pad: Math.max(w, h),
    };
  }

  /** Painted run-off and barrier panels, under the road and the armco. */
  private drawSponsorPaint(ctx: CanvasRenderingContext2D): void {
    ctx.globalAlpha = 0.82;
    for (const g of this.groundPaint) {
      ctx.fillStyle = g.sponsor.bg;
      ctx.fill(g.path);
      ctx.lineWidth = 0.35;
      ctx.strokeStyle = g.sponsor.trim;
      ctx.stroke(g.path);
    }
    ctx.globalAlpha = 1;
  }

  private drawBarrierPaint(ctx: CanvasRenderingContext2D, cam: Camera): void {
    for (const b of this.barrierPaint) {
      if (!cam.isVisible(b.x, b.y, 12)) continue;
      ctx.fillStyle = b.sponsor.bg;
      ctx.fill(b.path);
    }
  }

  /** Every sponsor wordmark, blitted at its real size in world metres. */
  private drawDecals(ctx: CanvasRenderingContext2D, cam: Camera): void {
    for (const d of this.decals) {
      if (!cam.isVisible(d.x, d.y, d.pad + 6)) continue;
      ctx.save();
      ctx.translate(d.x, d.y);
      ctx.rotate(d.angle);
      ctx.drawImage(d.img, -d.w / 2, -d.h / 2, d.w, d.h);
      ctx.restore();
    }
  }

  private buildBoards(half: number, kerbWidth: number): void {
    const { center, normal } = this.track;
    const offset = half + kerbWidth + 2.2;
    for (const corner of this.corners) {
      // Outside of the corner, where a driver would actually be looking.
      const side = -corner.direction;
      for (const board of corner.boards) {
        const i = board.index;
        this.boards.push({
          x: center[i].x + normal[i].x * offset * side,
          y: center[i].y + normal[i].y * offset * side,
          label: String(board.distance),
        });
      }
    }
  }

  draw(ctx: CanvasRenderingContext2D, cam: Camera, showReferenceLine: boolean): void {
    ctx.save();
    cam.applyTransform(ctx);

    // Under everything: the paint is on the ground, and the road is opaque.
    ctx.fillStyle = this.c.shoulder;
    ctx.fill(this.shoulder, 'evenodd');

    // Gravel/paved run-off, if this circuit authored any — under the kerbs
    // and walls that will draw on top of its inner edge, over the plain
    // grass background it replaces.
    for (const band of this.runoffBands) {
      ctx.fillStyle = band.kind === 'gravel' ? this.c.gravel : this.c.asphalt;
      ctx.fill(band.path);
    }

    // Scenery stands on the grass behind the barrier, so it goes under
    // everything the circuit itself is made of.
    for (const l of this.landmarkShapes) {
      ctx.fillStyle = l.fill;
      ctx.fill(l.path);
      ctx.lineWidth = 0.4;
      ctx.strokeStyle = l.edge;
      ctx.stroke(l.path);
    }

    this.drawSponsorPaint(ctx);

    ctx.fillStyle = this.c.asphalt;
    ctx.fill(this.road, 'evenodd');

    // Water goes under the braking zones: you should still be able to read the
    // zone through it, because knowing both at once is the whole point.
    ctx.fillStyle = this.c.water;
    for (const w of this.water) {
      ctx.globalAlpha = w.alpha;
      ctx.fill(w.path);
    }
    ctx.globalAlpha = 1;

    for (let i = 0; i < this.kerbPaths.length; i++) {
      ctx.fillStyle = this.kerbColours[i % this.kerbColours.length];
      ctx.fill(this.kerbPaths[i]);
    }

    ctx.lineWidth = 0.22;
    ctx.strokeStyle = this.c.asphaltEdge;
    ctx.stroke(this.edgeL);
    ctx.stroke(this.edgeR);

    // Barrier. A rail plus posts, so the outer limit is read as a wall rather
    // than an arbitrary line in the grass.
    ctx.lineWidth = 0.55;
    ctx.strokeStyle = this.c.wallRail;
    ctx.stroke(this.wallL);
    ctx.stroke(this.wallR);
    ctx.fillStyle = this.c.wallPost;
    for (const p of this.wallPosts) {
      ctx.fillRect(p.x - 0.3, p.y - 0.3, 0.6, 0.6);
    }
    this.drawBarrierPaint(ctx, cam);

    ctx.fillStyle = this.c.brakeZone;
    for (const z of this.zones) {
      ctx.globalAlpha = z.alpha;
      ctx.fill(z.path);
    }
    ctx.globalAlpha = 0.85;
    ctx.fillStyle = this.c.brakeLine;
    for (const line of this.brakeLines) ctx.fill(line);

    ctx.globalAlpha = 0.5;
    ctx.fillStyle = this.c.sectorLine;
    for (const band of this.sectorBands) ctx.fill(band);
    ctx.globalAlpha = 1;

    this.drawStartBand(ctx);
    // Wordmarks last inside the world transform, so nothing paints over them.
    this.drawDecals(ctx, cam);
    if (showReferenceLine) this.drawReferenceLine(ctx);
    ctx.restore();

    this.drawBoards(ctx, cam);
  }

  private drawStartBand(ctx: CanvasRenderingContext2D): void {
    ctx.save();
    ctx.clip(this.startBand);
    const { center, normal, startIndex, width } = this.track;
    const c = center[startIndex];
    const nrm = normal[startIndex];
    const squares = 10;
    const step = width / squares;
    for (let i = 0; i < squares; i++) {
      const t = -width / 2 + i * step;
      ctx.fillStyle = i % 2 === 0 ? COLORS.startLine : '#1c1e22';
      ctx.beginPath();
      ctx.rect(c.x + nrm.x * t - 2, c.y + nrm.y * t - 2, 4, 4);
      ctx.fill();
    }
    ctx.restore();
    ctx.fillStyle = COLORS.startLine;
    ctx.globalAlpha = 0.9;
    ctx.fill(this.startBand);
    ctx.globalAlpha = 1;
  }

  /**
   * Racing line coloured by what to *do*, not by how fast you are going.
   *
   * Red where the ideal profile is shedding speed, amber where it is holding it
   * through the corner, green where it is back on the power. That is the
   * convention every driving-line assist uses, and it is far more actionable
   * than a speed gradient: a speed gradient tells you a corner is slow, this
   * tells you the brakes should already be on.
   */
  private drawReferenceLine(ctx: CanvasRenderingContext2D): void {
    const { center, spacing } = this.track;
    const n = center.length;
    const v = this.profile.vIdeal;

    ctx.lineWidth = 0.7;
    ctx.lineCap = 'round';
    for (let i = 0; i < n; i++) {
      const j = wrapIndex(i + 1, n);
      // Rate of change of speed over distance, converted to an acceleration.
      const accel = (v[j] * v[j] - v[i] * v[i]) / (2 * spacing);
      const colour =
        accel < -1.5
          ? '#e03b2f' // braking
          : accel > 1.5
            ? '#3ecf7a' // on the power
            : '#e8c14a'; // neutral through the apex
      ctx.strokeStyle = colour;
      ctx.beginPath();
      ctx.moveTo(center[i].x, center[i].y);
      ctx.lineTo(center[j].x, center[j].y);
      ctx.stroke();
    }
  }

  /** Boards are drawn in screen space so the text stays legible at any zoom. */
  private drawBoards(ctx: CanvasRenderingContext2D, cam: Camera): void {
    ctx.save();
    ctx.setTransform(cam.dpr, 0, 0, cam.dpr, 0, 0);
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    const w = 22;
    const h = 18;
    for (const b of this.boards) {
      if (!cam.isVisible(b.x, b.y, 12)) continue;
      const { x: sx, y: sy } = cam.worldToScreen(b.x, b.y);
      ctx.fillStyle = 'rgba(0,0,0,0.45)';
      ctx.fillRect(sx - w / 2 + 1.5, sy - h / 2 + 1.5, w, h);
      ctx.fillStyle = COLORS.boardFace;
      ctx.fillRect(sx - w / 2, sy - h / 2, w, h);
      ctx.fillStyle = COLORS.boardText;
      ctx.font = '700 12px ui-monospace, Menlo, monospace';
      ctx.fillText(b.label, sx, sy + 0.5);
    }
    ctx.restore();
  }

  drawGrass(ctx: CanvasRenderingContext2D, cam: Camera): void {
    ctx.setTransform(cam.dpr, 0, 0, cam.dpr, 0, 0);
    ctx.fillStyle = COLORS.grass;
    ctx.fillRect(0, 0, cam.viewW, cam.viewH);

    // Faint 20 m banding so speed is legible even away from the track edge.
    ctx.save();
    cam.applyTransform(ctx);
    ctx.fillStyle = COLORS.grassAlt;
    const band = 20;
    const r = cam.visibleRadius();
    const startY = Math.floor((cam.y - r) / band) * band;
    for (let y = startY; y < cam.y + r; y += band * 2) {
      ctx.fillRect(cam.x - r, y, r * 2, band);
    }
    ctx.restore();
  }
}
